import type { UpdateEntityMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateEntityDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export type UpdateEntityTRPCInput = {
  Id: string;
  /**
   * Optimistic-concurrency token (COI-622) — the entity row's last-seen
   * `ModifiedAtTimestamp`. The legacy `updateChildEntity` mutation has no such
   * variable, so `graphqlVariables` below (which picks fields explicitly) does
   * not forward it; only the tRPC path locks on it.
   */
  OriginalTimestamp?: string | null;
  Name: string;
  Description?: string | null;
  ParentId?: string | null;
  Weight: number;
  OwnerUserIds?: string[] | null;
  OwnerGroupIds?: string[] | null;
};

/**
 * The tRPC update returns `{ Id }`; the GraphQL shape wants `affected_rows`,
 * so we map to 1 on success.
 */
export const useUpdateEntity = createMutationHook({
  name: 'updateEntity',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.entity.update.mutationOptions({ onSuccess }),
  mapInput: (variables: UpdateEntityTRPCInput) => variables,
  invalidate: (trpc) => [
    trpc.frontend.entity.register.queryKey(),
    trpc.frontend.entity.getById.queryKey(),
    // COI-889: this hook rewrites the owner/contributor sets, whose triggers
    // maintain the grant cache `perm_v2_entity_permissions` reads (60-min
    // window, `refetchOnMount: false`, so it never self-heals).
    trpc.frontend.permission.getEntityPermissions.queryKey(),
  ],
  mapResponse: (): UpdateEntityMutation => ({
    updateChildEntity: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateEntityDocument,
  graphqlVariables: (variables: UpdateEntityTRPCInput) => ({
    Id: variables.Id,
    Name: variables.Name,
    Description: variables.Description,
    ParentId: variables.ParentId,
    Weight: variables.Weight,
    owners: variables.OwnerUserIds ?? [],
    ownerGroups: variables.OwnerGroupIds ?? [],
  }),
  evictFields: ['entity', 'entity_by_pk'],
});
